import type { PropsWithChildren } from 'react'
import { useLayoutEffect, useState } from 'react'

import { useDisablePortalContext } from './disable-portal-context'
import { DisablePortalProvider } from './disable-portal-provider'
import { Portal } from '../portal/portal'

type Props = PropsWithChildren<{
    disablePortal?: boolean
}>

/**
 * As recommended by react docs https://reactjs.org/docs/portals.html#event-bubbling-through-portals
 * > ...portal elements are inserted into the DOM tree after the Modal's children are mounted, meaning that children will be mounted on a detached DOM node.
 * > ...to measure a DOM node, or uses 'autoFocus' in a descendant, add state to Modal and only render the children when Modal is inserted in the DOM tree
 *
 * To have `autoFocus`  work, we need to render the dialog after the portal has been inserted into the DOM tree.
 */

export const DialogContainer = ({ children, disablePortal }: Props) => {
    const [show, setShow] = useState(false)
    const disablePortalDefault = useDisablePortalContext()
    const shouldDisablePortal = disablePortal ?? disablePortalDefault

    // eslint-disable-next-line @eslint-react/set-state-in-effect
    useLayoutEffect(() => setShow(true), [setShow])

    if (shouldDisablePortal) {
        return <DisablePortalProvider value={false}>{children}</DisablePortalProvider>
    }

    return (
        <DisablePortalProvider value={false}>
            <Portal>{show && children}</Portal>
        </DisablePortalProvider>
    )
}

DialogContainer.displayName = 'DialogContainer'
